import type { z } from 'zod'

/**
 * A schema-guarded accessor for Web Storage.
 *
 * `docs/standards/trust-boundaries.md` lists storage reads and `JSON.parse`
 * reconstructions as trust boundaries: whatever is in `localStorage` was
 * written by an older build, a different tab, or a user with devtools open,
 * and is not the shape this build expects just because it was ours once.
 * Before this, `app/utils/sanctumTokenStorage.ts` was the only owner of that
 * boundary, and it only ever reads opaque strings — nothing existed for
 * structured values.
 *
 * Reading is total: absent, unparseable, and stale-shape all resolve to
 * "nothing stored" rather than an error. A caller persisting a convenience
 * value has no useful way to act on "your saved data was corrupt", so raising
 * would only turn a silent recovery into a visible failure.
 */
interface SchemaStorageOptions<TValue> {
  /** Storage key. Namespace it, e.g. `mealz.draft.partner-registration`. */
  key: string
  /** Validates every read. Anything it rejects is discarded. */
  schema: z.ZodType<TValue>
  /**
   * Resolved per call rather than captured, because the composable that owns
   * it may be constructed during SSR where no storage object exists yet.
   * Defaults to `localStorage`; pass `sessionStorage` for tab-scoped state.
   */
  storage?: () => Storage
}

interface SchemaStorage<TValue> {
  /** The stored value, or undefined when nothing usable is stored. */
  read: () => TValue | undefined
  write: (value: TValue) => void
  clear: () => void
}

const defaultStorage = (): Storage => localStorage

export const createSchemaStorage = <TValue>({
  key,
  schema,
  storage = defaultStorage
}: SchemaStorageOptions<TValue>): SchemaStorage<TValue> => {
  /**
   * Storage access itself throws, not just parsing: Safari in private mode
   * and a full quota both reject `setItem`, and a disabled-storage profile
   * rejects the getter. Every entry point goes through this.
   */
  const withStorage = <TResult>(use: (store: Storage) => TResult): TResult | undefined => {
    if (typeof window === 'undefined') return undefined

    try {
      return use(storage())
    } catch {
      return undefined
    }
  }

  const read = (): TValue | undefined =>
    withStorage((store) => {
      const raw = store.getItem(key)
      if (raw === null) return undefined

      // Two separate failures, one outcome: not JSON at all, or JSON that no
      // longer matches the schema because the form's fields changed shape
      // since it was written.
      let parsed: unknown
      try {
        parsed = JSON.parse(raw)
      } catch {
        return undefined
      }

      const result = schema.safeParse(parsed)
      return result.success ? result.data : undefined
    })

  const write = (value: TValue): void => {
    withStorage((store) => {
      store.setItem(key, JSON.stringify(value))
    })
  }

  const clear = (): void => {
    withStorage((store) => {
      store.removeItem(key)
    })
  }

  return { clear, read, write }
}
